import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import {
  Settings,
  Building2,
  Plane,
  Ship,
  Bell,
  Users,
  DollarSign,
  CheckCircle2,
  Save,
  Globe,
  Zap,
  Shield,
  AlertCircle,
  RefreshCw,
  Lock,
  MapPin,
  Plus,
  Trash2,
  Upload,
  Loader2,
  Pencil,
} from 'lucide-react';

const CompanySettingsManager = () => {
  const { t, language } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [enabledFeatures, setEnabledFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Récupérer les paramètres
      const settingsResponse = await fetch(`${backendUrl}/api/company/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        setSettings(settingsData);
        setEnabledFeatures(settingsData.enabled_features || {});
      } else {
        setError(t('settingsLoadError'));
      }

    } catch (err) {
      setError(t('connectionErrorLoading'));
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updatedSettings) => {
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/company/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedSettings)
      });

      if (response.ok) {
        const result = await response.json();
        setSettings(result.settings);
        setEnabledFeatures(result.settings.enabled_features || {});
        setSuccessMessage(t('settingsUpdatedSuccess'));
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || t('settingsUpdateError'));
      }
    } catch (err) {
      setError(t('connectionErrorUpdating'));
      console.error('Error updating settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleGeneralUpdate = (field, value) => {
    const updatedSettings = {
      ...settings,
      general: {
        ...settings.general,
        [field]: value
      }
    };
    setSettings(updatedSettings);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError(t('invalidImageType') || 'Type de fichier non autorise');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError(t('fileTooLarge') || 'Fichier trop volumineux (max 2 Mo)');
      return;
    }

    try {
      setUploadingLogo(true);
      setError('');
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${backendUrl}/api/company/settings/upload-logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        handleGeneralUpdate('logo_url', data.logo_url);
        setSuccessMessage(t('logoUploadSuccess') || 'Logo mis a jour');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || t('logoUploadError') || 'Erreur lors de l\'upload du logo');
      }
    } catch (err) {
      setError(t('connectionErrorUpdating') || 'Erreur de connexion');
      console.error('Error uploading logo:', err);
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleTransportUpdate = (field, value) => {
    const updatedSettings = {
      ...settings,
      transport: {
        ...settings.transport,
        [field]: value
      }
    };
    setSettings(updatedSettings);
  };

  const handleCommunicationUpdate = (field, value) => {
    const updatedSettings = {
      ...settings,
      communication: {
        ...settings.communication,
        [field]: value
      }
    };
    setSettings(updatedSettings);
  };

  const handleFinancialUpdate = (field, value) => {
    const updatedSettings = {
      ...settings,
      financial: {
        ...settings.financial,
        [field]: value
      }
    };
    setSettings(updatedSettings);
  };

  const saveSettings = async () => {
    const updatePayload = {
      general: settings.general,
      transport: settings.transport,
      communication: settings.communication,
      financial: settings.financial
    };

    await updateSettings(updatePayload);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin mr-2" />
        <span>{t('loadingSettings')}</span>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="p-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t('cannotLoadCompanySettings')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec actions */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="h-6 w-6" />
          {t('companySettings')}
        </h2>

        <Button
          onClick={saveSettings}
          disabled={saving}
        >
          {saving ? (
            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {saving ? t('saving') : t('save')}
        </Button>
      </div>

      {/* Messages */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {successMessage && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      {/* Fonctionnalités activées */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            {t('enabledFeatures')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.entries(enabledFeatures)
              .filter(([feature]) => !feature.startsWith('_'))
              .map(([feature, enabled]) => {
                const planFeatures = enabledFeatures._plan_features || [];
                const hasAll = planFeatures.includes('all');
                const featurePlanMap = {
                  whatsapp_notifications: 'whatsapp',
                  whatsapp_ai_responses: 'whatsapp_ai_responses',
                  broadcast: 'broadcast',
                  advanced_analytics: 'advanced_analytics',
                  exchange_rate_api: 'finance',
                };
                const requiredPlanFeature = featurePlanMap[feature];
                const lockedByPlan = requiredPlanFeature && !hasAll && !planFeatures.includes(requiredPlanFeature);

                return (
                  <Badge
                    key={feature}
                    className={
                      lockedByPlan
                        ? 'bg-accent/10 text-accent border border-accent/20'
                        : enabled
                        ? 'bg-green-100 text-green-800'
                        : 'bg-muted text-muted-foreground'
                    }
                  >
                    {lockedByPlan ? (
                      <Lock className="h-3 w-3 mr-1 inline" />
                    ) : enabled ? '✅ ' : '❌ '}
                    {t(feature)}
                  </Badge>
                );
              })}
          </div>
          {enabledFeatures._plan_features && !enabledFeatures._plan_features.includes('all') && (
            <p className="text-xs text-orange-500 mt-3 flex items-center gap-1">
              <Lock className="h-3 w-3" />
              {t('featureLockedByPlan')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Onglets de configuration */}
      <Tabs defaultValue="general" className="w-full">
        <TabsList className="flex w-full h-auto gap-1 overflow-x-auto">
          <TabsTrigger value="general" className="flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-3">
            <Building2 className="h-4 w-4 shrink-0 sm:mr-2" />
            <span className="hidden sm:inline">{t('generalSettings')}</span>
            <span className="sm:hidden">{language === 'en' ? 'General' : 'Général'}</span>
          </TabsTrigger>
          <TabsTrigger value="transport" className="flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-3">
            <Plane className="h-4 w-4 shrink-0 sm:mr-2" />
            <span className="hidden sm:inline">{t('transportSettings')}</span>
            <span className="sm:hidden">Transport</span>
          </TabsTrigger>
          <TabsTrigger value="communication" className="flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-3">
            <Bell className="h-4 w-4 shrink-0 sm:mr-2" />
            <span className="hidden sm:inline">{t('communicationSettings')}</span>
            <span className="sm:hidden">{language === 'en' ? 'Notif.' : 'Notif.'}</span>
          </TabsTrigger>
          <TabsTrigger value="financial" className="flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-3">
            <DollarSign className="h-4 w-4 shrink-0 sm:mr-2" />
            <span className="hidden sm:inline">{t('financialSettings')}</span>
            <span className="sm:hidden">{language === 'en' ? 'Finance' : 'Finance'}</span>
          </TabsTrigger>
        </TabsList>

        {/* Paramètres généraux */}
        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('generalSettings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('companyName')}</Label>
                  <Input
                    value={settings.general?.company_name || ''}
                    onChange={(e) => handleGeneralUpdate('company_name', e.target.value)}
                    placeholder={t('companyNamePlaceholder')}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('companyType')}</Label>
                  <Select
                    value={settings.general?.company_type || ''}
                    onValueChange={(value) => handleGeneralUpdate('company_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('chooseCompanyType')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="international_freight">{t('international_freight')}</SelectItem>
                      <SelectItem value="local_partner">{t('local_partner')}</SelectItem>
                      <SelectItem value="transit_logistics">{t('transit_logistics')}</SelectItem>
                      <SelectItem value="warehouse_operator">{t('warehouse_operator')}</SelectItem>
                      <SelectItem value="customs_broker">{t('customs_broker')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('primaryCurrency')}</Label>
                  <Select
                    value={settings.general?.primary_currency || 'EUR'}
                    onValueChange={(value) => handleGeneralUpdate('primary_currency', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">{t('currencyEur')}</SelectItem>
                      <SelectItem value="USD">{t('currencyUsd')}</SelectItem>
                      <SelectItem value="XAF">{t('currencyXaf')}</SelectItem>
                      <SelectItem value="GBP">{t('currencyGbp')}</SelectItem>
                      <SelectItem value="CAD">{t('currencyCad')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('originCountry')}</Label>
                  <Input
                    value={settings.general?.origin_country || ''}
                    onChange={(e) => handleGeneralUpdate('origin_country', e.target.value)}
                    placeholder="France"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('primaryColor')}</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={settings.general?.primary_color || '#059669'}
                      onChange={(e) => handleGeneralUpdate('primary_color', e.target.value)}
                      className="w-20"
                    />
                    <Input
                      value={settings.general?.primary_color || '#059669'}
                      onChange={(e) => handleGeneralUpdate('primary_color', e.target.value)}
                      placeholder="#059669"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>{t('companyLogo') || 'Logo entreprise'}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t('logoUploadHint') || 'Importez une image ou collez une URL (max 2 Mo)'}
                  </p>

                  {settings.general?.logo_url && (
                    <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                      <img
                        src={settings.general.logo_url}
                        alt="Logo"
                        className="h-16 w-16 object-contain rounded border bg-white"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <span className="text-xs text-muted-foreground truncate flex-1">
                        {settings.general.logo_url}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploadingLogo}
                      onClick={() => document.getElementById('logo-upload-input').click()}
                      className="flex items-center gap-2"
                    >
                      {uploadingLogo ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      {uploadingLogo ? (t('uploading') || 'Upload...') : (t('uploadImage') || 'Importer une image')}
                    </Button>
                    <input
                      id="logo-upload-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                  </div>

                  <Input
                    value={settings.general?.logo_url || ''}
                    onChange={(e) => handleGeneralUpdate('logo_url', e.target.value)}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Points Relais */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                {t('relayPoints')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(settings.general?.relay_points || []).map((rp, index) => (
                <div key={rp.id || index} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm">{rp.name || t('relayPoint') + ` ${index + 1}`}</h4>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={rp.is_active !== false}
                        onCheckedChange={(checked) => {
                          const updated = [...(settings.general?.relay_points || [])];
                          updated[index] = { ...updated[index], is_active: checked };
                          handleGeneralUpdate('relay_points', updated);
                        }}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                        onClick={() => {
                          const updated = (settings.general?.relay_points || []).filter((_, i) => i !== index);
                          handleGeneralUpdate('relay_points', updated);
                        }}
                        title={t('deleteRelayPoint')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">{t('relayPointName')}</Label>
                      <Input
                        value={rp.name || ''}
                        onChange={(e) => {
                          const updated = [...(settings.general?.relay_points || [])];
                          updated[index] = { ...updated[index], name: e.target.value };
                          handleGeneralUpdate('relay_points', updated);
                        }}
                        placeholder={t('relayPointName')}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('relayPointAddress')}</Label>
                      <Input
                        value={rp.address || ''}
                        onChange={(e) => {
                          const updated = [...(settings.general?.relay_points || [])];
                          updated[index] = { ...updated[index], address: e.target.value };
                          handleGeneralUpdate('relay_points', updated);
                        }}
                        placeholder={t('relayPointAddress')}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('relayPointCity')}</Label>
                      <Input
                        value={rp.city || ''}
                        onChange={(e) => {
                          const updated = [...(settings.general?.relay_points || [])];
                          updated[index] = { ...updated[index], city: e.target.value };
                          handleGeneralUpdate('relay_points', updated);
                        }}
                        placeholder={t('relayPointCity')}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('relayPointPhone')}</Label>
                      <Input
                        value={rp.phone || ''}
                        onChange={(e) => {
                          const updated = [...(settings.general?.relay_points || [])];
                          updated[index] = { ...updated[index], phone: e.target.value };
                          handleGeneralUpdate('relay_points', updated);
                        }}
                        placeholder={t('relayPointPhone')}
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('relayPointOpeningHours')}</Label>
                    <Input
                      value={rp.opening_hours || ''}
                      onChange={(e) => {
                        const updated = [...(settings.general?.relay_points || [])];
                        updated[index] = { ...updated[index], opening_hours: e.target.value };
                        handleGeneralUpdate('relay_points', updated);
                      }}
                      placeholder="Lun-Ven 8h-18h, Sam 9h-13h"
                    />
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  const newRelayPoint = {
                    id: crypto.randomUUID(),
                    name: '',
                    address: '',
                    city: '',
                    phone: '',
                    opening_hours: '',
                    is_active: true
                  };
                  const updated = [...(settings.general?.relay_points || []), newRelayPoint];
                  handleGeneralUpdate('relay_points', updated);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('addRelayPoint')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Paramètres de transport */}
        <TabsContent value="transport" className="space-y-4">
          {/* Modes de transport */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {t('transportModes')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('selectTransportModes')}
              </p>
              <div className="flex gap-4">
                {[
                  { id: 'aerien', label: t('airFreight'), icon: Plane },
                  { id: 'maritime', label: t('seaFreight'), icon: Ship },
                ].map(({ id, label, icon: Icon }) => {
                  const modes = settings.transport?.transport_modes || ['aerien'];
                  const isActive = modes.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => {
                        const updated = isActive
                          ? modes.filter(m => m !== id)
                          : [...modes, id];
                        if (updated.length === 0) return;
                        const currentDefault = settings.transport?.default_transport_mode || 'aerien';
                        const newDefault = updated.includes(currentDefault) ? currentDefault : updated[0];
                        setSettings(prev => ({
                          ...prev,
                          transport: {
                            ...prev.transport,
                            transport_modes: updated,
                            default_transport_mode: newDefault,
                          }
                        }));
                      }}
                      className={`flex items-center gap-3 px-5 py-3 rounded-lg border-2 transition-all ${
                        isActive
                          ? 'border-accent bg-accent/10 text-accent'
                          : 'border-slate-200 text-muted-foreground hover:border-slate-300'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{label}</span>
                      {isActive && <CheckCircle2 className="h-4 w-4" />}
                    </button>
                  );
                })}
              </div>

              {(settings.transport?.transport_modes || ['aerien']).length > 1 && (
                <div className="space-y-2">
                  <Label>{t('defaultMode')}</Label>
                  <Select
                    value={settings.transport?.default_transport_mode || 'aerien'}
                    onValueChange={(value) => handleTransportUpdate('default_transport_mode', value)}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(settings.transport?.transport_modes || ['aerien']).map(mode => (
                        <SelectItem key={mode} value={mode}>
                          {mode === 'aerien' ? t('airFreightShort') : t('seaFreightShort')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('transportSettings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('mainAirline')}</Label>
                  <Input
                    value={settings.transport?.main_airline || ''}
                    onChange={(e) => handleTransportUpdate('main_airline', e.target.value)}
                    placeholder="Air France, Turkish Airlines..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('averageBagWeight')}</Label>
                  <Input
                    type="number"
                    value={settings.transport?.average_bag_weight || 25}
                    onChange={(e) => handleTransportUpdate('average_bag_weight', parseFloat(e.target.value))}
                    placeholder="25"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('collectionMode')}</Label>
                  <Select
                    value={settings.transport?.collection_mode || 'both'}
                    onValueChange={(value) => handleTransportUpdate('collection_mode', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="depot_only">{t('depot_only')}</SelectItem>
                      <SelectItem value="home_collection">{t('home_collection')}</SelectItem>
                      <SelectItem value="both">{t('both')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('travelFrequency')}</Label>
                  <Select
                    value={settings.transport?.travel_frequency || 'weekly'}
                    onValueChange={(value) => handleTransportUpdate('travel_frequency', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">{t('weekly')}</SelectItem>
                      <SelectItem value="biweekly">{t('biweekly')}</SelectItem>
                      <SelectItem value="monthly">{t('monthly')}</SelectItem>
                      <SelectItem value="on_demand">{t('on_demand')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('averageCustomsTimeHours')}</Label>
                <Input
                  type="number"
                  value={settings.transport?.average_customs_time_hours || 24}
                  onChange={(e) => handleTransportUpdate('average_customs_time_hours', parseInt(e.target.value))}
                  placeholder="24"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={settings.transport?.flight_api_enabled || false}
                  onCheckedChange={(checked) => handleTransportUpdate('flight_api_enabled', checked)}
                />
                <Label>{t('flightApiEnabled')}</Label>
              </div>

              {/* Tracking Mode selector */}
              <Card className="border-dashed">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">{t('trackingMode')}</CardTitle>
                  <p className="text-xs text-muted-foreground">{t('trackingModeDescription')}</p>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const planFeatures = enabledFeatures._plan_features || [];
                    const hasAll = planFeatures.includes('all');
                    const autoAvailable = hasAll || planFeatures.includes('auto_tracking');
                    const currentMode = settings.transport?.tracking_mode || 'manual';
                    return (
                      <div className="flex gap-3">
                        <button
                          type="button"
                          className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${currentMode === 'manual' ? 'border-accent bg-accent/5' : 'border-border hover:border-muted-foreground/30'}`}
                          onClick={() => handleTransportUpdate('tracking_mode', 'manual')}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Pencil className="h-4 w-4 text-accent" />
                            <span className="font-medium text-sm">{t('trackingModeManual')}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{t('trackingModeManualDesc')}</p>
                        </button>
                        <button
                          type="button"
                          disabled={!autoAvailable}
                          className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${!autoAvailable ? 'opacity-50 cursor-not-allowed border-border' : currentMode === 'automatic' ? 'border-accent bg-accent/5' : 'border-border hover:border-muted-foreground/30'}`}
                          onClick={() => autoAvailable && handleTransportUpdate('tracking_mode', 'automatic')}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            {autoAvailable ? <Zap className="h-4 w-4 text-accent" /> : <Lock className="h-4 w-4 text-orange-500" />}
                            <span className="font-medium text-sm">{t('trackingModeAutomatic')}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{t('trackingModeAutoDesc')}</p>
                          {!autoAvailable && (
                            <Badge variant="outline" className="mt-2 text-xs text-orange-500 border-orange-300">
                              {t('trackingModeProRequired')}
                            </Badge>
                          )}
                        </button>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Mode de chargement des colis */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Mode de chargement</CardTitle>
                  <p className="text-xs text-muted-foreground">Comment les colis rejoignent un départ.</p>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const currentLoad = settings.transport?.loading_mode || 'manual';
                    return (
                      <div className="flex gap-3">
                        <button
                          type="button"
                          className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${currentLoad === 'manual' ? 'border-accent bg-accent/5' : 'border-border hover:border-muted-foreground/30'}`}
                          onClick={() => handleTransportUpdate('loading_mode', 'manual')}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Pencil className="h-4 w-4 text-accent" />
                            <span className="font-medium text-sm">Manuel</span>
                          </div>
                          <p className="text-xs text-muted-foreground">On assigne les colis au départ, puis on charge.</p>
                        </button>
                        <button
                          type="button"
                          className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${currentLoad === 'auto' ? 'border-accent bg-accent/5' : 'border-border hover:border-muted-foreground/30'}`}
                          onClick={() => handleTransportUpdate('loading_mode', 'auto')}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Zap className="h-4 w-4 text-accent" />
                            <span className="font-medium text-sm">Auto (scan)</span>
                          </div>
                          <p className="text-xs text-muted-foreground">Le scan au chargement assigne et charge le colis. Réduit les erreurs.</p>
                        </button>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Paramètres de communication */}
        <TabsContent value="communication" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('communicationSettings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('senderEmail')}</Label>
                  <Input
                    type="email"
                    value={settings.communication?.sender_email || ''}
                    onChange={(e) => handleCommunicationUpdate('sender_email', e.target.value)}
                    placeholder="noreply@company.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('senderName')}</Label>
                  <Input
                    value={settings.communication?.sender_name || ''}
                    onChange={(e) => handleCommunicationUpdate('sender_name', e.target.value)}
                    placeholder={t('yourCompanyPlaceholder')}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('whatsappNumber')}</Label>
                  <Input
                    value={settings.communication?.whatsapp_number || ''}
                    onChange={(e) => handleCommunicationUpdate('whatsapp_number', e.target.value)}
                    placeholder="+33612345678"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('cronFrequencyHours')}</Label>
                  <Select
                    value={settings.communication?.cron_frequency_hours?.toString() || '12'}
                    onValueChange={(value) => handleCommunicationUpdate('cron_frequency_hours', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">{`6 ${t('hours')}`}</SelectItem>
                      <SelectItem value="12">{`12 ${t('hours')}`}</SelectItem>
                      <SelectItem value="24">{`24 ${t('hours')}`}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4">
                {(() => {
                  const planFeatures = enabledFeatures._plan_features || [];
                  const hasAll = planFeatures.includes('all');
                  const aiLocked = !hasAll && !planFeatures.includes('whatsapp_ai_responses');
                  return (
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={settings.communication?.ai_service_enabled || false}
                        onCheckedChange={(checked) => handleCommunicationUpdate('ai_service_enabled', checked)}
                        disabled={aiLocked}
                      />
                      <Label className={aiLocked ? 'text-gray-400' : ''}>
                        {t('aiServiceEnabled')}
                        {aiLocked && <Lock className="h-3 w-3 ml-1 inline text-orange-500" />}
                      </Label>
                      {aiLocked && (
                        <span className="text-xs text-orange-500">Enterprise</span>
                      )}
                    </div>
                  );
                })()}

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={settings.communication?.sms_api_enabled || false}
                    onCheckedChange={(checked) => handleCommunicationUpdate('sms_api_enabled', checked)}
                  />
                  <Label>{t('smsApiEnabled')}</Label>
                </div>
              </div>

              {/* Canaux de diffusion */}
              <div className="pt-4 border-t space-y-3">
                <Label className="text-base font-semibold">{t('broadcastChannels')}</Label>
                <p className="text-sm text-gray-500">
                  {t('selectBroadcastChannels')}
                </p>
                <div className="space-y-2">
                  {[
                    { id: 'email', label: 'Email' },
                    { id: 'whatsapp', label: 'WhatsApp' },
                    { id: 'in_app', label: t('inAppNotification') },
                  ].map(({ id, label }) => {
                    const channels = settings.communication?.broadcast_channels || ['email', 'whatsapp'];
                    return (
                      <div key={id} className="flex items-center space-x-2">
                        <Switch
                          checked={channels.includes(id)}
                          onCheckedChange={(checked) => {
                            const updated = checked
                              ? [...channels, id]
                              : channels.filter(c => c !== id);
                            handleCommunicationUpdate('broadcast_channels', updated);
                          }}
                        />
                        <Label>{label}</Label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Paramètres financiers */}
        <TabsContent value="financial" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('financialSettings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('defaultMarginPercent')}</Label>
                  <Input
                    type="number"
                    value={settings.financial?.default_margin_percent || 10}
                    onChange={(e) => handleFinancialUpdate('default_margin_percent', parseFloat(e.target.value))}
                    placeholder="10"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={settings.financial?.auto_tax_reporting || false}
                    onCheckedChange={(checked) => handleFinancialUpdate('auto_tax_reporting', checked)}
                  />
                  <Label>{t('autoTaxReporting')}</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={settings.financial?.exchange_rate_api_enabled || false}
                    onCheckedChange={(checked) => handleFinancialUpdate('exchange_rate_api_enabled', checked)}
                  />
                  <Label>{t('exchangeRateApiEnabled')}</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={settings.financial?.expense_reports_enabled || false}
                    onCheckedChange={(checked) => handleFinancialUpdate('expense_reports_enabled', checked)}
                  />
                  <Label>{language === 'en' ? 'Delivery expense reports' : 'Notes de frais livreurs'}</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CompanySettingsManager;